<script lang="ts">
	import type { CompleteVariant, RequiredProduct } from '$lib/types';
	import Image from '$lib/components/Image/Image.svelte';
	import { parseImageUrls } from '$lib/utils/image';
	import { getTranslate, T } from '@tolgee/svelte';
	import { classes } from '@thearc-hq/ui-kit/utils';
	import StoryblokVideo from '$lib/components/Storyblok/StoryblokVideo.svelte';
	import { createBreakpointStore } from '$lib/store/breakpoint.store';
	import ButtonTertiary from '$lib/components/Button/ButtonTertiary.svelte';

	export let variantImageUrls: CompleteVariant['image_urls'] | undefined;
	export let productImageUrls: RequiredProduct['image_urls'];
	export let variantThreeDPreview: CompleteVariant['dreid'] | undefined;
	export let productThreeDPreview: RequiredProduct['dreid'] | undefined;
	export let productVideoId: RequiredProduct['video'] | undefined;
	export let variantVideoId: CompleteVariant['video'] | undefined;

	const { t } = getTranslate();

	$: parsedImageData = {
		parsedVariantImageUrls: parseImageUrls(variantImageUrls),
		parsedProductImageUrls: parseImageUrls(productImageUrls)
	};

	$: parsedVariantImageUrls = parsedImageData.parsedVariantImageUrls;
	$: parsedProductImageUrls = parsedImageData.parsedProductImageUrls;
	let images: (string | undefined)[];
	let showMore = false;

	$: images =
		((parsedVariantImageUrls?.length ?? 0) > 0 ? parsedVariantImageUrls : parsedProductImageUrls) ||
		[];

	const mobileStore = createBreakpointStore();
	$: isMobile = $mobileStore;
	$: threeDPreview = variantThreeDPreview?.value || productThreeDPreview?.value;
	$: video = variantVideoId?.value || productVideoId?.value;
	$: displayedImages = (() => {
		let local = Array.from(images);

		if (threeDPreview) {
			local = [...local.slice(0, 1), undefined, ...local.slice(1)];
		}

		if (video) {
			local = [...local.slice(0, 2), undefined, ...local.slice(2)];
		}

		return local;
	})();

	// Desktop LightBox logic (lazy)
	let LightBox: typeof import('$lib/components/Product/LightBox.svelte').default | undefined;

	let showLightBox = false;
	let lightBoxSelectedImage = 0;

	const _loadLightBox = (retries = 0) => {
		import('$lib/components/Product/LightBox.svelte')
			.then((module) => {
				LightBox = module.default;
			})
			.catch((e) => {
				if (retries < 3) {
					_loadLightBox(retries + 1);
				} else {
					throw e;
				}
			});
	};

	$: if (showLightBox && !LightBox) {
		_loadLightBox();
	}

	// Mobile Scroll Container Logic
	let scrollContainer = undefined;
	let scrollIndex = 0;
	const onScroll = (e) => {
		scrollIndex = Math.round(e.target.scrollLeft / e.target.clientWidth);
	};
	const scrollTo = (index: number) => {
		if (!scrollContainer) {
			return;
		}
		scrollContainer.scrollTo({
			left: index * scrollContainer.clientWidth,
			behavior: 'smooth'
		});
	};
</script>

<svelte:component
	this={LightBox}
	bind:isShown={showLightBox}
	threeD={threeDPreview}
	videoId={video}
	imageUrls={parsedVariantImageUrls?.length ? parsedVariantImageUrls : parsedProductImageUrls}
	bind:selectedImageIndex={lightBoxSelectedImage}
/>

<div class="relative">
	<div
		bind:this={scrollContainer}
		on:scroll={onScroll}
		class={classes(
			'flex overflow-x-auto snap-x snap-mandatory md:grid',
			images.length > 3 ? `md:grid-cols-2 md:gap-5` : 'grid-rows-1'
		)}
	>
		{#each images.length > 3 || isMobile ? displayedImages : displayedImages.slice(0, 1) as url, i}
			{@const showThreeDPreview = i === 1 && !url}
			{@const showVideoEmbed = i === 2 && !url}
			{@const isFirstImage = i === 0}

			{#if (isMobile ? true : i < (threeDPreview ? 5 : 4) || showMore) && (showThreeDPreview ? isMobile : true)}
				<div
					class="group relative cursor-pointer w-full flex-none snap-center"
					on:click={() => {
						if (isMobile) {
							return;
						}
						if (showVideoEmbed) {
							lightBoxSelectedImage = 3;
						} else {
							lightBoxSelectedImage = i;
						}

						showLightBox = true;
					}}
				>
					{#if showThreeDPreview}
						<div class="md:hidden w-full h-full">
							<button
								on:click={() => scrollTo(i - 1)}
								class="left-0 top-1/2 transform -translate-y-1/2 absolute rounded-full hover:border-accent-primary border p-3 bg-white"
							>
								<div class="i-sprenger-chevron-left w-6 h-6"></div>
							</button>
							<iframe src={threeDPreview} frameborder="0" scrolling="no" class="w-full h-full" />
							<button
								on:click={() => scrollTo(i + 1)}
								class="right-0 top-1/2 transform -translate-y-1/2 absolute rounded-full hover:border-accent-primary border p-3 bg-white"
							>
								<div class="i-sprenger-chevron-right w-6 h-6"></div>
							</button>
						</div>
					{:else if showVideoEmbed}
						<button
							on:click={() => scrollTo(i - 1)}
							class="md:hidden left-0 top-1/2 transform -translate-y-1/2 absolute rounded-full hover:border-accent-primary border p-3 bg-white"
						>
							<div class="i-sprenger-chevron-left w-6 h-6"></div>
						</button>
						<StoryblokVideo video_id={video} autoplay />
						<button
							on:click={() => scrollTo(i + 1)}
							class="md:hidden right-0 top-1/2 transform -translate-y-1/2 absolute rounded-full hover:border-accent-primary border p-3 bg-white"
						>
							<div class="i-sprenger-chevron-right w-6 h-6"></div>
						</button>
					{:else}
						<Image
							src={url}
							fetchpriority={isFirstImage ? 'high' : ''}
							loading={isFirstImage ? 'eager' : 'lazy'}
							decoding={isFirstImage ? 'sync' : 'async'}
							shouldShowLoad
							sizes={images.length > 3
								? '(min-width: 768px) 34vw, 100vw'
								: '(min-width: 768px) 70vw, 100vw'}
							width={768}
							height={768}
						/>
					{/if}

					<div
						class="hidden md:block group-hover:opacity-100 opacity-0 transition duration-500 left-1/2 top-1/2 absolute bg-white transform group-hover:-translate-y-1/2 -translate-x-1/2 rounded-full p-2"
					>
						<div class="i-sprenger-maximize-2 bg-accent-primary w-6 h-6"></div>
					</div>
					{#if i === 0 && threeDPreview}
						<!-- This needs stopPropagation to make sure the parent of this element does not overwrite the lightBoxSelectedImage -->
						<button
							on:click|stopPropagation={() => {
								lightBoxSelectedImage = 1;
								showLightBox = true;
							}}
							class="hidden top-0 right-0 absolute md:flex flex-col items-center hover:bg-dark-quaternary rounded-lg p-2"
						>
							<div class="i-sprenger-three-d-preview w-8 h-8 mx-auto"></div>
							<span class="text-p-12">
								<T keyName="360_degree" />
							</span>
						</button>
					{/if}
				</div>
			{/if}
		{/each}
	</div>
	{#if images.length > 4}
		<ButtonTertiary
			class="!hidden md:!block mx-auto mt-8"
			text={showMore ? $t('product.images.showLess') : $t('product.images.showMore')}
			on:click={() => (showMore = !showMore)}
		/>
	{/if}
	<!-- These are the image dots maybe add an onclick listener? -->
	<div class="absolute bottom-2 w-full md:hidden">
		<div class="flex justify-center items-center gap-2">
			{#each displayedImages as _, i}
				<div
					class="bg-accent-primary h-2 w-2 rounded-full transition-height duration-500"
					class:h-3={i === scrollIndex}
					class:w-3={i === scrollIndex}
				></div>
			{/each}
		</div>
	</div>
</div>
